import {
  ButtonDS,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  triggerToast,
  usePersistMuiTableState,
} from "@qivia/ui";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useAppSelector } from "../../redux/hooks";
import {
  selectCardsListStatus,
  selectCardsList,
  selectUnblockPinOfflineStatus,
} from "./cardsSlice";
import { UnblockPinOfflineModal } from "./modals/unblockPinOfflineModal";
import { selectAdminUser } from "../homeSlice";
import {
  GRID_AGGREGATION_ROOT_FOOTER_ROW_ID,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { ViewPinCodeModal } from "./modals/viewPinCodeModal";

export const UnblockPinOfflineTab = () => {
  const params = useParams();
  const { t } = useTranslation();
  const cardsListStatus = useAppSelector(selectCardsListStatus);
  const unblockPinOfflineStatus = useAppSelector(selectUnblockPinOfflineStatus);
  const cardsList = useAppSelector(selectCardsList);
  const adminUser = useAppSelector(selectAdminUser);

  const pageName = "cards_shipments";
  const apiRef = useGridApiRef();
  const key = "unblock-pin-offline-grid";
  const aggregation = useMemo(
    () => ({
      model: {
        registrationNumber: "size",
        vehicleReference: "size",
        identifier: "size",
        last4Digits: "size",
        status: "size",
      },
    }),
    [],
  );

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  const [unblockPinOfflineModalVisible, setUnblockPinOfflineModalVisible] =
    useState<boolean>(false);
  const [viewPinCardUuid, setViewPinCardUuid] = useState<string | null>(null);
  const [viewPinCardLabel, setViewPinCardLabel] = useState<string | undefined>(
    undefined,
  );

  const blockedCards = useMemo(
    () => cardsList.filter((c) => c.status === "BLOCKED_PIN_OFFLINE"),
    [cardsList],
  );

  type RowDisplayed = {
    uuid: string;
    registrationNumber: string | null;
    vehicleReference: string | null;
    identifier: string | null;
    last4Digits: string | null;
    status: string;
    actions: string;
  };

  const rowsWithId: RowDisplayed[] = useMemo(
    () =>
      blockedCards.map((c) => ({
        uuid: c.uuid,
        registrationNumber: c.registrationNumber,
        vehicleReference: c.vehicleReference,
        identifier: c.identifier,
        last4Digits: c.last4Digits,
        status: t(`cards.status.${c.status}`),
        actions: "",
        id: c.uuid,
      })) as (RowDisplayed & { id: string })[],
    [blockedCards, t],
  );

  const [listDisplayed, setListDisplayed] = useState<RowDisplayed[]>([]);

  useEffect(() => {
    if (unblockPinOfflineStatus === "success") {
      triggerToast(
        t("cards_shipments.pin.toast.unblockPinOffline.success") || "",
        "valid",
      );
    } else if (unblockPinOfflineStatus === "failed") {
      triggerToast(
        t("cards_shipments.pin.toast.unblockPinOffline.error") || "",
        "error",
      );
    }
  }, [unblockPinOfflineStatus, t]);

  useEffect(() => {
    setListDisplayed(rowsWithId);
  }, [rowsWithId]);

  const columnsDisplayed: {
    [K in keyof RowDisplayed]: MaterialUiColumnsProps;
  } = useMemo(
    () => ({
      uuid: {
        type: "string",
        width: 320,
      },
      registrationNumber: { type: "string", width: 160 },
      vehicleReference: { type: "string", width: 180 },
      identifier: { type: "string", width: 160 },
      last4Digits: { type: "string", width: 120 },
      status: { type: "string", width: 220 },
      actions: {
        type: "string",
        width: 200,
        renderCell: (params) => {
          if (params.id === GRID_AGGREGATION_ROOT_FOOTER_ROW_ID) {
            return <></>;
          }

          const row = params.row as RowDisplayed;
          return (
            <StyledButton>
              <ButtonDS
                text={t("cards_shipments.pin.button.viewPin")}
                onClick={() =>
                  (() => {
                    setViewPinCardUuid(row.uuid);
                    setViewPinCardLabel(
                      row.last4Digits
                        ? `**** ${row.last4Digits}`
                        : (row.identifier ?? undefined),
                    );
                  })()
                }
                format="fill"
                buttonType="primary"
              />
            </StyledButton>
          );
        },
      },
    }),
    [t],
  );

  const searchBarProps = useMemo(
    () => ({
      values: rowsWithId,
      setFilterValues: (filteredValues: RowDisplayed[]) =>
        setListDisplayed(filteredValues),
      keysToIgnore: ["actions" as const, "id" as const],
    }),
    [rowsWithId],
  );

  if (cardsListStatus === "processing") {
    return <LaunchPage hasBorderRadius={true} />;
  }

  if (params.tab !== "unblockPinOffline") {
    return;
  }

  if (adminUser?.email === "exterieur1@qivia.fr") {
    return;
  }

  return (
    <StyledContainer>
      <Spacer y={1} />
      <StyledActionsRow>
        <ButtonDS
          text={t("cards_shipments.pin.button.unblockPinOffline")}
          onClick={() => setUnblockPinOfflineModalVisible(true)}
          format="hug"
          buttonType="secondary"
        />
      </StyledActionsRow>
      <Spacer y={1} />
      <MaterialUiTable<keyof RowDisplayed, RowDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        apiRef={apiRef}
        searchBar={searchBarProps}
      />
      <UnblockPinOfflineModal
        cardsUuidAccepted={blockedCards.map((c) => c.uuid)}
        visible={unblockPinOfflineModalVisible}
        onCloseModal={() => setUnblockPinOfflineModalVisible(false)}
      />
      <ViewPinCodeModal
        visible={!!viewPinCardUuid}
        onCloseModal={() => {
          setViewPinCardUuid(null);
          setViewPinCardLabel(undefined);
        }}
        cardUuid={viewPinCardUuid}
        cardLabel={viewPinCardLabel}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledActionsRow = styled.div`
  display: flex;
  align-items: center;
`;
const StyledButton = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
`;
